import { AnyAction } from '@reduxjs/toolkit'

import type { FetchStatusCommon } from 'restApi/types'

type ChangeRequestData = {
  id: number
  start_date: string
  end_date: string
  total_guests: number
  adults: number
  children: number
  status: string
  original_price: string
  new_price: string
  price_difference: string
  changed: any[]
  can_accept: boolean
  calculated_refund: {
    voucher_refund_amount: string
    total_refund: string
  }
  customer_service_fee: string
  tax_amount: string
  special_offer_price: string
  by_host: boolean
  currency: string
  deferred_payment_eligible: boolean
  due_now_amount: string
  due_on_amount: string
  due_on_date: string
  total_paid: string
  line_items: {
    item_type: string
    price: string
    quantity: number
    amount: string
    details: {
      date: string
      price: number
    }[]
  }[]
  tax_rate_details: {
    name: string
    rate: string
    juris_type: string
    included: boolean
  }[]
  listing: {
    id: number
    title: string
    description_truncated: string
    price: string
    pricing_suffix: string
    nice_pricing_suffix: string
    street: string
    street2: string
    city: string
    postcode: string
    state: string
    country_id: number
    region_id: number
    region_name: string
    location: {
      name: string
      lat: number
      lng: number
    }
    user_id: number
    promo_image: {
      medium: string
      card_portrait: string
      thumb: string
      large: string
      hero: string
    }
    url: string
    status: string
    review_count: number
    review_score: string
    cancellation_policy: string
    instant_book_enabled: boolean
    service_fee_included: boolean
    created_at: string
    updated_at: string
    description: string
    additional_info: string
    adventure_text: string
    listing_type_id: number
    listing_subcategory_id: number
    concurrent_bookings: boolean
    may_submit: boolean
    on_airbnb: boolean
    booking_window: number
    story_eligible: boolean
    giftable: boolean
    bookable: boolean
    instant_book_eligible: boolean
    same_day_bookings: boolean
    preparation_time_in_days: number
    onboarding: boolean
    max_security_deposit: string
    compliance_number: string
    compliance_number_exempt: number
    compliance_number_required: number
    currency: string
    tax_exclusive: boolean
    availability_score: number
    added_to_bucket_list: boolean
    capacity: number
    weekend_price: string
    weekend_price_includes_sunday: number
    extra_person_fee: string
    extra_child_fee: string
    extra_person_threshold: number
    cleaning_fee: string
    security_deposit: string
    check_in: string
    check_out: string
    bedrooms: number
    beds: number
    bathrooms: number
    pet_friendly: boolean
    minimum_booking_duration: number
    weekend_minimum_booking_duration: number
    amenity_ids: number[]
    outdoor_equipment_details: string
    check_in_instructions: string
  }
}

type ChangeRequestState = FetchStatusCommon & {
  data: ChangeRequestData
  error: Record<string, string>
}

const initialState: ChangeRequestState = {
  fetching: false,
  fetched: false,
  updating: false,
  updated: false,
  submitting: false,
  error: null,
  data: null,
}

export default (
  state = initialState,
  action: AnyAction
): ChangeRequestState => {
  switch (action.type) {
    case 'BOOKING_CHANGE_REQUEST_FETCH_PENDING':
      return {
        ...state,
        fetching: true,
      }

    case 'BOOKING_CHANGE_REQUEST_FETCH_REJECTED':
      return {
        ...state,
        fetching: false,
        error: action.payload.data,
      }

    case 'BOOKING_CHANGE_REQUEST_FETCH_FULFILLED':
      return {
        ...state,
        fetching: false,
        data: action.payload.data,
      }

    case 'BOOKING_CHANGE_REQUEST_SUBMIT_PENDING':
      return {
        ...state,
        updating: true,
        submitting: true,
      }

    case 'BOOKING_CHANGE_REQUEST_SUBMIT_REJECTED':
      return {
        ...state,
        updating: false,
        submitting: false,
        error: action.payload.data,
      }

    case 'BOOKING_CHANGE_REQUEST_SUBMIT_FULFILLED':
      return {
        ...state,
        updating: false,
        submitting: false,
        data: null,
      }

    case 'BOOKING_CHANGE_REQUEST_ACCEPT_PENDING':
      return {
        ...state,
        updating: true,
        updated: false,
      }

    case 'BOOKING_CHANGE_REQUEST_ACCEPT_REJECTED':
      return {
        ...state,
        updating: false,
        error: action.payload.data,
      }

    case 'BOOKING_CHANGE_REQUEST_ACCEPT_FULFILLED': {
      return {
        ...state,
        updating: false,
        updated: true,
        data: action.payload.data,
      }
    }

    case 'BOOKING_CHANGE_REQUEST_DECLINE_PENDING':
      return {
        ...state,
        updating: true,
        updated: false,
      }

    case 'BOOKING_CHANGE_REQUEST_DECLINE_REJECTED':
      return {
        ...state,
        updating: false,
        error: action.payload.data,
      }

    case 'BOOKING_CHANGE_REQUEST_DECLINE_FULFILLED': {
      return {
        ...state,
        updating: false,
        updated: true,
        data: action.payload.data,
      }
    }

    default:
      return state
  }
}
